/**
 * "Referrals" on the account page (PRP-MCP-10 work item 1): shown only to a
 * user who runs an affiliate code (`/me/referrals` lists one or more). Each
 * shows the links to share, how many people signed up through it, and earned,
 * paid and owed. No referred user's name or email (the server never sends one).
 */
import { useEffect, useState } from 'react';

import { Card } from './ui/card';
import { MyReferralsAPI, fmtCents } from '../../lib/adminAffiliatesApi';
import type { MyReferrals } from '../../lib/adminAffiliatesApi';

/**
 * Renders nothing for a user who is not an affiliate, or when the read fails.
 * `onAvailable` tells the Settings page whether to offer the Referrals tab.
 */
export default function ReferralsSection({ onAvailable }: { onAvailable?: (_shown: boolean) => void } = {}) {
  const [rows, setRows] = useState<MyReferrals[]>([]);

  useEffect(() => {
    let cancelled = false;
    MyReferralsAPI.get()
      .then((r) => {
        if (!cancelled) {
          setRows(r.affiliates ?? []);
          onAvailable?.((r.affiliates ?? []).length > 0);
        }
      })
      .catch(() => {
        /* optional section: the page renders without it */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (rows.length === 0) return null;
  return (
    <Card className="p-0" data-testid="referrals-section">
      <div className="px-[18px] pt-[16px] pb-[12px] border-b border-[var(--bd-subtle)]">
        <h2 className="m-0 text-[11px] font-[650] uppercase tracking-[.06em] text-[var(--tx-lo)]">Referrals</h2>
        <p className="mt-1 mb-0 text-[12px] text-[var(--tx-faint)]">
          You earn 20% of what the people you refer pay for their first 12 months. Payouts are monthly once you are
          owed {fmtCents(rows[0].minimum_payout_cents)} or more.
        </p>
      </div>
      <div className="p-[18px] space-y-4">
        {rows.map((r) => (
          <div key={r.code} className="space-y-2 text-[13px] text-[var(--tx-mid)]">
            <div>
              Your code <span className="font-mono text-[var(--tx-hi)]">{r.code}</span>
              {r.status === 'paused' ? ' (paused: new signups are not being referred)' : ''}
            </div>
            <ul className="m-0 list-none space-y-1 p-0">
              {r.links.map((l) => (
                <li key={l} className="break-all font-mono text-[12px] text-[var(--tx-hi)]">{l}</li>
              ))}
            </ul>
            <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
              {([
                ['Referrals', String(r.referrals)],
                ['Earned', fmtCents(r.earned_cents)],
                ['Paid', fmtCents(r.paid_cents)],
                ['Owed', fmtCents(r.owed_cents)],
              ] as const).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[var(--tx-lo)]">{k}</dt>
                  <dd className="m-0 text-[var(--tx-hi)]" data-testid={`referrals-${k.toLowerCase()}`}>{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </Card>
  );
}
